import { Navigate, Route, BrowserRouter, Routes } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LocationProvider } from "@/context/LocationContext";
import { ScannerProvider } from "@/context/ScannerContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { PrinterProvider } from "@/context/PrinterContext";
import { KioskProvider } from "@/context/KioskContext";
import { Toaster } from "sonner";
import { useKeyboardInset } from "@/hooks/useKeyboardInset";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Intake from "@/pages/Intake";
import Materials from "@/pages/Materials";
import Suppliers from "@/pages/Suppliers";
import Customers from "@/pages/Customers";
import Recipes from "@/pages/Recipes";
import Production from "@/pages/Production";
import Packing from "@/pages/Packing";
import Inventory from "@/pages/Inventory";
import Dispatch from "@/pages/Dispatch";
import Waste from "@/pages/Waste";
import Cleaning from "@/pages/Cleaning";
import Trace from "@/pages/Trace";
import Reports from "@/pages/Reports";
import Admin from "@/pages/Admin";
import StockTake from "@/pages/StockTake";
import KnifeCount from "@/pages/KnifeCount";

function Private({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-500">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Route guard for permission-keyed pages.
 * Renders `children` if the user has the permission; otherwise redirects to
 * the dashboard (which is itself permission-gated — admin always sees it,
 * other roles need the `dashboard` key, which is default-true).
 */
function PermitRoute({ permission, children }) {
  const { hasPermission, user } = useAuth();
  // Admin panel is reserved for the admin role — hard-gate on role to block
  // any future bug where the 'admin' permission key leaks onto another role.
  if (permission === "admin" && user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  if (!hasPermission(permission)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  // Track soft-keyboard height on tablets so DialogContent can shrink to
  // fit above it — see useKeyboardInset docstring for details.
  useKeyboardInset();
  // Aggressively remove any "Made with Emergent" badge injected by the
  // platform's remote script — the operator taps it by accident on shop-
  // floor tablets and leaves the app. Running at App root so it also
  // fires on /login where Layout is not mounted.
  useEffect(() => {
    const purge = () => {
      const el = document.getElementById("emergent-badge");
      if (el) el.remove();
    };
    purge();
    const obs = new MutationObserver(purge);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
        <Toaster position="bottom-right" richColors expand={false} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <Private>
                <LocationProvider>
                  <ScannerProvider>
                    <PrinterProvider>
                      <KioskProvider>
                        <Layout />
                      </KioskProvider>
                    </PrinterProvider>
                  </ScannerProvider>
                </LocationProvider>
              </Private>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="intake" element={<PermitRoute permission="intake"><Intake /></PermitRoute>} />
            <Route path="materials" element={<PermitRoute permission="materials"><Materials /></PermitRoute>} />
            <Route path="suppliers" element={<PermitRoute permission="suppliers"><Suppliers /></PermitRoute>} />
            <Route path="customers" element={<PermitRoute permission="customers"><Customers /></PermitRoute>} />
            <Route path="recipes" element={<PermitRoute permission="recipes"><Recipes /></PermitRoute>} />
            <Route path="production" element={<PermitRoute permission="production"><Production /></PermitRoute>} />
            <Route path="packing" element={<PermitRoute permission="packing"><Packing /></PermitRoute>} />
            <Route path="inventory" element={<PermitRoute permission="inventory"><Inventory /></PermitRoute>} />
            <Route path="dispatch" element={<PermitRoute permission="dispatch"><Dispatch /></PermitRoute>} />
            <Route path="waste" element={<PermitRoute permission="waste"><Waste /></PermitRoute>} />
            <Route path="knives" element={<PermitRoute permission="knives"><KnifeCount /></PermitRoute>} />
            <Route path="cleaning" element={<PermitRoute permission="cleaning"><Cleaning /></PermitRoute>} />
            <Route path="trace" element={<PermitRoute permission="trace"><Trace /></PermitRoute>} />
            <Route path="reports" element={<PermitRoute permission="reports"><Reports /></PermitRoute>} />
            <Route path="admin" element={<PermitRoute permission="admin"><Admin /></PermitRoute>} />
            <Route path="stock-take" element={<PermitRoute permission="admin"><StockTake /></PermitRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
